import Link from 'next/link';
import Modal from '@/components/Modals/Core';
import { useEnsureModalConversionPoint } from '@/components/Modals/hooks/useEnsureModalConversionPoint';
import useDirectionalAnimation from '@/components/Modals/Sidebar/hooks/useDirectionalAnimation';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import StyledPrice from '@/components/Modals/SwitchToYearly/components/StyledPrice';
import { Button, Loading } from '@music-vine/cadence/ui';
import { Text } from '@music-vine/cadence/ui';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useSubscriptionType } from '@/hooks/auth';
import { ProrationBillingMode, useProration, useUpgradeSubscription } from '@/hooks/billing/useProration';
import { isBusinessPlanName } from '@/lib/pricing/businessPlans';
import { useSettings } from '@/hooks/state/useSettings';
import CurrentPlanCard from './components/CurrentPlanCard';
import FinePrint from './components/FinePrint';
import UpgradeCard from './components/UpgradeCard';
import ModalSkeleton from './skeleton';
import { UpgradePlanModalScreenName } from './types';
import type { Plan } from '@/fixtures/plans';
enum BillingMode {
  MONTHLY = 1,
  YEARLY = 2,
}
interface UpgradePlanScreensProps {
  billingModeOverride?: ProrationBillingMode;
  plan: Plan['name'];
}
const UpgradePlanScreens = ({
  billingModeOverride,
  plan
}: UpgradePlanScreensProps) => {
  useEnsureModalConversionPoint(ModalConversionPoints.UpgradeModal);
  const animationProps = useDirectionalAnimation();
  const {
    settings
  } = useSettings();
  const {
    data: pricingOptions
  } = usePricingOptions();
  const subscriptionType = useSubscriptionType();
  const pricingOption = pricingOptions?.find(option => option.title === plan);

  // When the user already has an active subscription on the same tier and is
  // only switching billing period, use their existing Stripe plan ID so the
  // charge matches the proration preview (which also uses the original plan).
  // Matches on the effective tier so a legacy `Business` subscriber buying the
  // 2026 tier commits to the new plan rather than re-buying their own.
  const existingPlanId = plan === subscriptionType ? settings?.subscriptionData?.plan : undefined;
  const period: BillingMode = settings?.subscriptionData?.period ?? 1;

  // What the subscriber pays today, which is not necessarily the period being
  // bought — upgrading to annual-only Business from a monthly plan would
  // otherwise price their current plan yearly in the "Current plan" card.
  const currentBillingMode = period === BillingMode.YEARLY ? ProrationBillingMode.YEARLY : ProrationBillingMode.MONTHLY;

  // Business is annual-only, so it can't inherit a monthly period from the
  // subscriber's current plan (nor accept a monthly override).
  const billingMode = isBusinessPlanName(plan) ? ProrationBillingMode.YEARLY : billingModeOverride ?? currentBillingMode;
  const {
    data: prorationData,
    isLoading: isProrationLoading,
    isError: prorationError
  } = useProration({
    plan,
    billingMode
  });
  const isLoading = isProrationLoading;
  const {
    upgradeSubscriptionMutation: {
      mutate: upgradeSubscription,
      isPending: isUpdatingSubscription
    },
    isUpdatingRoles
  } = useUpgradeSubscription();
  const renderContent = () => {
    if (isLoading) {
      return <ModalSkeleton />;
    }

    // The response type allows `data: null` as a successful body, so route
    // to the error screen on either a failed query or a null body.
    if (prorationError || !prorationData) {
      return <>
          <Modal.Title className="mb-2 font-semibold text-2xl text-gray-950 dark:text-white">
            Upgrade subscription
          </Modal.Title>
          <p className="mb-8 text-red">
            We are currently unable to process your upgrade. Please try again
            later. If this problem persists, please contact us via our help
            center.
          </p>
          <Button asChild className="mb-8 w-full [&>span]:font-semibold! [&>span]:text-base!" size="lg" variant="error">
            <Link href="/help-center/contact-us">Contact us</Link>
          </Button>
        </>;
    }
    return <>
        <div>
          <Modal.Title className="mb-1 font-semibold text-2xl text-gray-950 dark:text-white" data-sentry-element="Modal.Title" data-sentry-source-file="screens.tsx">
            Upgrade subscription
          </Modal.Title>
          <Modal.Description className="mb-8" data-sentry-element="Modal.Description" data-sentry-source-file="screens.tsx">
            <Text variant="modal" data-sentry-element="Text" data-sentry-source-file="screens.tsx">You're switching to Uppbeat {plan}.</Text>
          </Modal.Description>
          <div className="mb-8 space-y-6">
            <CurrentPlanCard billingMode={currentBillingMode} data-sentry-element="CurrentPlanCard" data-sentry-source-file="screens.tsx" />
            <UpgradeCard billingMode={billingMode} plan={plan} data-sentry-element="UpgradeCard" data-sentry-source-file="screens.tsx" />
          </div>
        </div>

        <div className="mt-auto">
          <div className="mb-8 flex items-baseline justify-between gap-2">
            <Text className="text-gray-950 dark:text-white" data-sentry-element="Text" data-sentry-source-file="screens.tsx">Total amount:</Text>
            <StyledPrice classNames={{
            symbol: 'text-sm text-gray-800 dark:text-gray-300',
            integer: 'text-5xl font-sans font-semibold',
            separator: 'hidden',
            decimal: 'text-lg align-top font-semibold'
          }} currency={settings?.currency.code ?? 'GBP'} valueInMinorUnits={prorationData.total} data-sentry-element="StyledPrice" data-sentry-source-file="screens.tsx" />
          </div>

          <Button className="mb-5 w-full" disabled={isUpdatingSubscription || isUpdatingRoles} onClick={() => upgradeSubscription({
          plan,
          billingMode,
          proration_date: prorationData.prorationDate,
          value: prorationData.total,
          planSubscriptionId: existingPlanId ?? pricingOption?.subscription_stripe_plan_id ?? -1
        })} size="lg" variant="brand" data-sentry-element="Button" data-sentry-source-file="screens.tsx">
            <Loading visible={isUpdatingSubscription || isUpdatingRoles} data-sentry-element="Loading" data-sentry-source-file="screens.tsx" />
            {isUpdatingSubscription || isUpdatingRoles ? 'Processing payment' : 'Pay now'}
          </Button>
          <FinePrint billingMode={billingMode} fullPriceInPence={prorationData.followedBy} plan={plan} cardLast4={prorationData.cardLast4} prorationPriceInPence={prorationData.total} taxBehavior={prorationData.taxBehavior} taxInPence={prorationData.tax} data-sentry-element="FinePrint" data-sentry-source-file="screens.tsx" />
        </div>
      </>;
  };
  return <Modal.Screen className="flex grow flex-col px-6 pt-[52px] pb-20" screenName={UpgradePlanModalScreenName.UpgradePlan} {...animationProps} data-sentry-element="Modal.Screen" data-sentry-component="UpgradePlanScreens" data-sentry-source-file="screens.tsx">
      {renderContent()}
    </Modal.Screen>;
};
export default UpgradePlanScreens;